import { useState } from 'react';
import { enregistreFichier, fichierDocumentDemande, ouvreDocument } from '../../api/documents';
import { Card } from '../../components/ui/Card.jsx';
import { anneesDuDossier, contenuDuDossier, demandeDuDossier } from '../../lib/dossier-annee';
import { tr, trf } from '../../lib/i18n';
import { C } from '../../theme/palette';

const CHEMIN = '/depenses/dossier-annee';

/**
 * « Dossier de l'année » : tout ce qu'une fiduciaire, un contrôle fiscal ou
 * une régie demandent, en un seul PDF — le résumé des dépenses par type, les
 * tickets à plusieurs par feuille, chaque facture de fournisseur ou de
 * sous-traitant sur sa page (avec la facture client liée et le jour du
 * paiement), les dépenses mensuelles et les attestations de l'entreprise.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.soc l'entreprise
 * @param {object} props.pieces `{frais, chantiers, achatsJoints, factures, devis, attestations}`
 * @param {boolean} [props.magasin] univers Magasin : des fournisseurs, pas de sous-traitants
 * @returns {JSX.Element} la carte du classeur
 */
export function DossierAnnee({ soc, pieces, magasin = false }) {
  const annees = anneesDuDossier(pieces);
  const [annee, setAnnee] = useState(annees[0]);
  const [coche, setCoche] = useState({
    resume: true,
    tickets: true,
    achats: true,
    mensuelles: true,
    attestations: true,
  });
  const [enCours, setEnCours] = useState('');
  const [message, setMessage] = useState('');

  const contenu = contenuDuDossier(pieces, annee);
  const lignes = [
    {
      k: 'resume',
      lib: 'Résumé des dépenses par type',
      aide: 'Totaux avec TVA, TVA récupérable',
      nombre: tr('1 page'),
      present: true,
    },
    {
      k: 'tickets',
      lib: 'Tickets : resto, café, essence…',
      aide: 'Plusieurs tickets par feuille A4, par catégorie et par date',
      nombre: trf('{0} ticket(s)', contenu.tickets.length),
      present: contenu.tickets.length > 0,
    },
    {
      k: 'achats',
      // Un magasin n'a que des fournisseurs, pas de sous-traitants.
      lib: magasin ? 'Factures fournisseurs' : 'Factures fournisseurs et sous-traitants',
      aide: 'Chacune sur sa page, avec la facture client liée et le jour du paiement',
      nombre: trf('{0} facture(s)', contenu.achats.length),
      present: contenu.achats.length > 0,
    },
    {
      k: 'mensuelles',
      lib: 'Loyer, téléphone, assurances…',
      aide: 'Les justificatifs des dépenses mensuelles',
      nombre: trf('{0} pièce(s)', contenu.mensuelles.length),
      present: contenu.mensuelles.length > 0,
    },
    {
      k: 'attestations',
      lib: 'Attestations de l’entreprise',
      aide: 'RC entreprise, AVS, TVA, poursuites… (ou registre du commerce, statuts pour une Sàrl / SA)',
      nombre: trf('{0} document(s)', contenu.attestations.length),
      present: contenu.attestations.length > 0,
    },
  ];
  const rien = !lignes.some((l) => l.present && coche[l.k]);
  const nomFichier = trf('Dossier {0}', annee).replace(/\s+/g, '-') + '.pdf';

  const lance = async (action) => {
    setMessage('');
    setEnCours(action);
    const demande = demandeDuDossier(pieces, { annee, ...coche, magasin }, soc);
    try {
      if (action === 'imprimer') {
        await ouvreDocument(CHEMIN, undefined, demande);
        return;
      }
      const fichier = await fichierDocumentDemande(CHEMIN, demande, nomFichier);
      if (action === 'envoyer' && navigator.canShare?.({ files: [fichier] })) {
        try {
          await navigator.share({ files: [fichier], title: trf('Dossier {0}', annee) });
        } catch {
          // Partage annulé : rien d'autre à faire.
        }
        return;
      }
      enregistreFichier(fichier);
      if (action === 'envoyer') {
        setMessage(
          tr('Le PDF est enregistré : joins-le à ton e-mail pour la fiduciaire ou le contrôle.'),
        );
      }
    } catch (e) {
      if (!e?.dejaAffiche) setMessage(e?.message || tr('Le dossier n’a pas pu être créé.'));
    } finally {
      setEnCours('');
    }
  };

  const bouton = (action, texte, plein) => (
    <button
      type="button"
      disabled={rien || !!enCours}
      onClick={() => lance(action)}
      style={{
        flex: 1,
        padding: '11px 6px',
        borderRadius: 10,
        fontSize: 11.5,
        fontWeight: 800,
        cursor: rien || enCours ? 'default' : 'pointer',
        opacity: rien ? 0.5 : 1,
        border: '1.5px solid ' + C.navy,
        background: plein ? C.navy : '#fff',
        color: plein ? '#fff' : C.navy,
      }}
    >
      {enCours === action ? tr('Préparation…') : texte}
    </button>
  );

  return (
    <Card style={{ marginBottom: 6, overflow: 'hidden' }}>
      <details>
        <summary
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '12px 14px',
            cursor: 'pointer',
            listStyle: 'none',
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: C.red + '18',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              flexShrink: 0,
            }}
          >
            📦
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800 }}>{tr('Dossier de l’année')}</div>
            <div style={{ fontSize: 8.5, color: C.muted }}>
              {tr('Pour la fiduciaire, un contrôle fiscal ou une régie')}
            </div>
          </div>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.red }}>▸ {tr('voir')}</div>
        </summary>
        <div style={{ padding: '0 14px 14px' }}>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.muted, margin: '4px 0 6px' }}>
            {tr('ANNÉE')}
          </div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
            {annees.map((a) => (
              <button
                key={a}
                type="button"
                aria-pressed={annee === a}
                onClick={() => setAnnee(a)}
                style={{
                  padding: '7px 14px',
                  borderRadius: 9,
                  fontSize: 11.5,
                  fontWeight: 800,
                  cursor: 'pointer',
                  border: '1px solid ' + (annee === a ? C.navy : C.border),
                  background: annee === a ? C.navy : '#fff',
                  color: annee === a ? '#fff' : C.grey,
                }}
              >
                {a}
              </button>
            ))}
          </div>
          <div style={{ fontSize: 9, fontWeight: 800, color: C.muted, marginBottom: 4 }}>
            {tr('METTRE DANS LE DOSSIER')}
          </div>
          {lignes.map((l) => (
            <label
              key={l.k}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '9px 0',
                borderTop: '1px solid #eef2f6',
                cursor: 'pointer',
                opacity: l.present ? 1 : 0.5,
              }}
            >
              <input
                type="checkbox"
                checked={l.present && coche[l.k]}
                disabled={!l.present}
                onChange={(e) => setCoche({ ...coche, [l.k]: e.target.checked })}
                style={{ width: 18, height: 18, marginTop: 1, accentColor: C.teal }}
              />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 11.5, fontWeight: 800, color: C.ink }}>
                  {tr(l.lib)}
                </span>
                <span style={{ display: 'block', fontSize: 9, color: C.muted, marginTop: 2 }}>
                  {tr(l.aide)}
                </span>
              </span>
              <b style={{ fontSize: 10.5, color: C.ink, whiteSpace: 'nowrap' }}>{l.nombre}</b>
            </label>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            {bouton('imprimer', '🖨 ' + tr('Imprimer'), true)}
            {bouton('pdf', '📄 ' + tr('PDF'))}
            {bouton('envoyer', '✉️ ' + tr('Envoyer'))}
          </div>
          {message && (
            <div role="status" style={{ fontSize: 10, color: C.grey, marginTop: 8 }}>
              {message}
            </div>
          )}
        </div>
      </details>
    </Card>
  );
}
